/**
 * Pipeline Revenue Leak Assessment - Questions Data
 * 
 * Structure:
 * - sections: Array of quiz sections
 * - Each section contains questions
 * - Each question has answers with point values
 * - Scoring logic calculates per-section and overall scores
 */

export const quizConfig = {
  title: "How Much Pipeline Revenue Are You Losing?",
  subtitle: "A 2-minute diagnostic for founders",
  totalQuestions: 10,
  estimatedTime: "2 minutes"
};

export const sections = [
  {
    id: "context",
    name: "Quick Context",
    description: "Help us personalize your results",
    scorable: false, // These questions don't contribute to score
    questions: [
      {
        id: "q1",
        questionNumber: 1,
        text: "What's your approximate annual revenue?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q1-a", 
            text: "Under $250k", 
            value: "<250k",
            revenueMultiplier: 0.15,
            revenueMidpoint: 150000
          },
          { 
            id: "q1-b", 
            text: "$250k - $500k", 
            value: "250k-500k",
            revenueMultiplier: 0.18,
            revenueMidpoint: 375000
          },
          { 
            id: "q1-c", 
            text: "$500k - $1M", 
            value: "500k-1m",
            revenueMultiplier: 0.20,
            revenueMidpoint: 750000
          },
          { 
            id: "q1-d", 
            text: "$1M - $3M", 
            value: "1m-3m",
            revenueMultiplier: 0.18,
            revenueMidpoint: 2000000
          },
          { 
            id: "q1-e", 
            text: "$3M - $5M", 
            value: "3m-5m",
            revenueMultiplier: 0.15,
            revenueMidpoint: 4000000
          },
          { 
            id: "q1-f", 
            text: "$5M+", 
            value: "5m+",
            revenueMultiplier: 0.12,
            revenueMidpoint: 6000000
          }
        ]
      },
      {
        id: "q2",
        questionNumber: 2,
        text: "What industry are you in?",
        type: "single-select",
        required: true,
        answers: [
          { id: "q2-a", text: "Real Estate / Brokerage", value: "real-estate" },
          { id: "q2-b", text: "Medical / Dental / Healthcare", value: "medical" },
          { id: "q2-c", text: "Professional Services (Legal, Accounting, Consulting)", value: "professional-services" },
          { id: "q2-d", text: "Education / Schools", value: "education" },
          { id: "q2-e", text: "Home Services / Trades", value: "home-services" },
          { id: "q2-f", text: "Other Service Business", value: "other" }
        ]
      }
    ]
  },
  {
    id: "top-of-funnel",
    name: "Top of Funnel",
    description: "Lead generation & initial response",
    scorable: true,
    maxPoints: 20,
    questions: [
      {
        id: "q3",
        questionNumber: 3,
        text: "How do most leads find you?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q3-a", 
            text: "Referrals only - no active lead generation", 
            value: "referrals-only",
            points: 0,
            insight: "Relying solely on referrals limits growth and makes revenue unpredictable."
          },
          { 
            id: "q3-b", 
            text: "Some marketing, but inconsistent", 
            value: "inconsistent-marketing",
            points: 5,
            insight: "Inconsistent marketing creates feast-or-famine lead flow."
          },
          { 
            id: "q3-c", 
            text: "Consistent lead generation system in place", 
            value: "consistent-system",
            points: 10,
            insight: null // No issue to flag
          }
        ]
      },
      {
        id: "q4",
        questionNumber: 4,
        text: "When a new lead comes in, what happens?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q4-a", 
            text: "I respond when I get to it (hours or days)", 
            value: "delayed-response",
            points: 0,
            insight: "Slow response time kills conversions. Studies show response rates drop 10x after the first hour."
          },
          { 
            id: "q4-b", 
            text: "Same-day response, but it's manual", 
            value: "manual-same-day",
            points: 5,
            insight: "Manual responses are better, but you're the bottleneck. What happens when you're busy?"
          },
          { 
            id: "q4-c", 
            text: "Automated response + scheduled follow-up", 
            value: "automated-response",
            points: 10,
            insight: null
          }
        ]
      }
    ]
  },
  {
    id: "middle-of-funnel",
    name: "Middle of Funnel",
    description: "Nurturing & follow-up systems",
    scorable: true,
    maxPoints: 30,
    questions: [
      {
        id: "q5",
        questionNumber: 5,
        text: "What happens to leads who aren't ready to buy right now?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q5-a", 
            text: "Nothing - they sit until I remember them", 
            value: "nothing",
            points: 0,
            insight: "Leads who aren't nurtured go cold. Most of your pipeline value is sitting in 'not ready yet' leads."
          },
          { 
            id: "q5-b", 
            text: "Occasional manual follow-up", 
            value: "occasional-manual",
            points: 5,
            insight: "Sporadic follow-up is better than nothing, but consistency is what converts."
          },
          { 
            id: "q5-c", 
            text: "Systematic nurture sequence in place", 
            value: "systematic-nurture",
            points: 10,
            insight: null
          }
        ]
      },
      {
        id: "q6",
        questionNumber: 6,
        text: "How many leads are sitting idle with no contact in 30+ days?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q6-a", 
            text: "No idea", 
            value: "no-idea",
            points: 0,
            insight: "If you don't know, you can't fix it. These forgotten leads represent real revenue."
          },
          { 
            id: "q6-b", 
            text: "Probably a lot", 
            value: "probably-many",
            points: 3,
            insight: "Awareness is the first step. Now you need visibility into exactly who they are."
          },
          { 
            id: "q6-c", 
            text: "Some, but I'm aware of them", 
            value: "some-aware",
            points: 7,
            insight: "Good awareness. The next step is a system to re-engage them automatically."
          },
          { 
            id: "q6-d", 
            text: "I know the exact number", 
            value: "exact-number",
            points: 10,
            insight: null
          }
        ]
      },
      {
        id: "q7",
        questionNumber: 7,
        text: "How much of your \"nurturing\" relies on your personal time each week?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q7-a", 
            text: "All of it - if I stop, it stops", 
            value: "all-personal",
            points: 0,
            insight: "You are the bottleneck. Your pipeline health is tied to your availability."
          },
          { 
            id: "q7-b", 
            text: "Most of it, with some automation", 
            value: "mostly-personal",
            points: 5,
            insight: "Some automation helps, but you're still doing most of the heavy lifting."
          },
          { 
            id: "q7-c", 
            text: "Mostly automated, I just review", 
            value: "mostly-automated",
            points: 10,
            insight: null
          }
        ]
      }
    ]
  },
  {
    id: "bottom-of-funnel",
    name: "Bottom of Funnel",
    description: "Closing & retention",
    scorable: true,
    maxPoints: 30,
    questions: [
      {
        id: "q8",
        questionNumber: 8,
        text: "When deals fall through, do you know why?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q8-a", 
            text: "Not really - they just disappear", 
            value: "no-visibility",
            points: 0,
            insight: "Without knowing why deals fail, you can't fix the pattern. You're losing the same way repeatedly."
          },
          { 
            id: "q8-b", 
            text: "I have hunches but no data", 
            value: "hunches-only",
            points: 5,
            insight: "Hunches are a start, but data reveals patterns you can't see intuitively."
          },
          { 
            id: "q8-c", 
            text: "I track reasons and address patterns", 
            value: "track-and-address",
            points: 10,
            insight: null
          }
        ]
      },
      {
        id: "q9",
        questionNumber: 9,
        text: "After someone becomes a client, do you have a system for referrals and reviews?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q9-a", 
            text: "No system - I sometimes ask", 
            value: "no-system",
            points: 0,
            insight: "Happy clients are your best lead source. Without a system, you're leaving referrals on the table."
          },
          { 
            id: "q9-b", 
            text: "I ask manually when I remember", 
            value: "manual-ask",
            points: 5,
            insight: "Manual asks work, but timing matters. An automated ask at the right moment converts better."
          },
          { 
            id: "q9-c", 
            text: "Automated ask built into the process", 
            value: "automated-ask",
            points: 10,
            insight: null
          }
        ]
      },
      {
        id: "q10",
        questionNumber: 10,
        text: "If you were away for 30 days, how much of your pipeline would keep moving?",
        type: "single-select",
        required: true,
        answers: [
          { 
            id: "q10-a", 
            text: "Almost nothing - it all depends on me", 
            value: "nothing-moves",
            points: 0,
            insight: "Your business stops when you stop. That's not a pipeline - it's a job that owns you."
          },
          { 
            id: "q10-b", 
            text: "Some things would continue", 
            value: "some-continues",
            points: 5,
            insight: "Partial automation is progress. The goal is a pipeline that works while you don't."
          },
          { 
            id: "q10-c", 
            text: "Most of it runs without me", 
            value: "mostly-runs",
            points: 10,
            insight: null
          }
        ]
      }
    ]
  }
];

/**
 * Lead capture form configuration
 */
export const leadCaptureConfig = {
  title: "You're done! Let's see your results.",
  subtitle: "Enter your details to unlock your Pipeline Health Report:",
  fields: [
    {
      id: "fullName",
      label: "Full Name",
      type: "text",
      placeholder: "John Smith",
      required: true,
      validation: {
        minLength: 2,
        errorMessage: "Please enter your full name"
      }
    },
    {
      id: "email",
      label: "Email Address",
      type: "email",
      placeholder: "john@company.com",
      required: true,
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        errorMessage: "Please enter a valid email address"
      }
    },
    {
      id: "companyName",
      label: "Company Name",
      type: "text",
      placeholder: "Acme Corp",
      required: true,
      validation: {
        minLength: 2,
        errorMessage: "Please enter your company name"
      }
    },
    {
      id: "companyWebsite",
      label: "Company Website",
      type: "url",
      placeholder: "https://acmecorp.com",
      required: true,
      validation: {
        pattern: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
        errorMessage: "Please enter a valid website URL"
      }
    }
  ],
  benefits: [
    "Your complete pipeline assessment",
    "Specific recommendations for your biggest gaps",
    "Actionable next steps based on your score"
  ],
  submitButtonText: "UNLOCK MY RESULTS →"
};

/**
 * Results page configuration
 */
export const resultsConfig = {
  gaugeLabels: {
    "top-of-funnel": "Top of Funnel",
    "middle-of-funnel": "Middle of Funnel",
    "bottom-of-funnel": "Bottom of Funnel"
  },
  gaugeDescriptions: {
    "top-of-funnel": "Lead generation & initial response",
    "middle-of-funnel": "Nurturing & follow-up systems",
    "bottom-of-funnel": "Closing & retention"
  },
  scoreThresholds: {
    poor: 40,      // 0-40% = red
    moderate: 70,  // 41-70% = yellow
    good: 100      // 71-100% = green
  },
  ctaPrimary: {
    text: "Book a Discovery Call",
    subtext: "Let's identify which leak to plug first",
    duration: "15-minute conversation",
    url: "YOUR_ZOHO_BOOKING_URL_HERE" // Replace with actual URL
  },
  ctaSecondary: {
    text: "Email me these results",
    enabled: false // Enable in V2
  }
};

/**
 * Dynamic insights based on section scores
 */
export const sectionInsights = {
  "top-of-funnel": {
    poor: "Your lead generation is heavily dependent on referrals and manual effort. New opportunities aren't finding you consistently.",
    moderate: "You have some lead generation in place, but there's room to systematize your initial response and capture.",
    good: "Your top of funnel is solid. Leads are coming in and getting prompt attention."
  },
  "middle-of-funnel": {
    poor: "Leads who aren't ready to buy immediately are going cold. This is likely your biggest revenue leak.",
    moderate: "You're doing some nurturing, but it's inconsistent. Warm leads are slipping through the cracks.",
    good: "Your nurturing systems are working. Leads stay warm until they're ready to buy."
  },
  "bottom-of-funnel": {
    poor: "You don't have visibility into why deals fail or systems to capture referrals. Revenue is leaking at the finish line.",
    moderate: "You're closing deals but missing opportunities to learn from losses and leverage wins for referrals.",
    good: "Your close process is strong with good feedback loops and referral systems."
  }
};

/**
 * Overall score messaging
 */
export const overallInsights = {
  poor: {
    headline: "Major Pipeline Leaks Detected",
    message: "Your pipeline has significant gaps at multiple stages. The good news: these are fixable, and addressing even one could meaningfully impact your revenue."
  },
  moderate: {
    headline: "Moderate Pipeline Leaks Detected",
    message: "You've got some systems in place, but there are clear opportunities to tighten things up. Focused improvements could recover substantial revenue."
  },
  good: {
    headline: "Pipeline Health: Strong",
    message: "Your pipeline is well-structured. At this stage, gains come from optimization and scaling what's already working."
  }
};

/**
 * Helper function to get all questions as a flat array
 */
export const getAllQuestions = () => {
  return sections.flatMap(section => section.questions);
};

/**
 * Helper function to get question by ID
 */
export const getQuestionById = (questionId) => {
  return getAllQuestions().find(q => q.id === questionId);
};

/**
 * Helper function to get section by ID
 */
export const getSectionById = (sectionId) => {
  return sections.find(s => s.id === sectionId);
};

/**
 * Helper function to get section for a question
 */
export const getSectionForQuestion = (questionId) => {
  return sections.find(section => 
    section.questions.some(q => q.id === questionId)
  );
};

export default {
  quizConfig,
  sections,
  leadCaptureConfig,
  resultsConfig,
  sectionInsights,
  overallInsights,
  getAllQuestions,
  getQuestionById,
  getSectionById,
  getSectionForQuestion
};
